/* crm-timeline-p4.jsx — Timeline Tab Phase 4 (Enterprise UI/UX)
   Provides: TimelineEventP4, CommunicationTimeline, CustomerTimelineView
   All data fetched live from backend. Uses CSS system classes throughout. */

const { useState, useEffect, useMemo, useCallback } = React;

/* ── helpers ── */
const _relTimeP4 = (iso) => {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 1)  return 'just now';
  if (m < 60) return m + 'm ago';
  const h = Math.floor(m / 60);
  if (h < 24) return h + 'h ago';
  const d = Math.floor(h / 24);
  if (d < 7)  return d + 'd ago';
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
};

const _dayKey = (iso) => {
  if (!iso) return 'Unknown';
  const d    = new Date(iso);
  const now  = new Date();
  const diff = Math.floor((now - d) / 86400000);
  if (diff === 0) return 'Today';
  if (diff === 1) return 'Yesterday';
  if (diff < 7)  return d.toLocaleDateString(undefined, { weekday: 'long' });
  return d.toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' });
};

const _getDateStr = (ev) =>
  ev.createdAt || ev.date || ev.timestamp || ev.sentAt || ev.scheduledAt || ev.completedAt || null;

const _normAct = (a) => ({
  id:        a.id,
  type:      (a.type || 'activity').toLowerCase(),
  who:       a.actor || a.who || '—',
  what:      a.description || a.what || '',
  detail:    a.detail || '',
  subject:   a.subject || a.title || '',
  createdAt: a.createdAt || null,
  personId:  a.personId  || null,
  companyId: a.companyId || null,
  dealId:    a.dealId    || null,
  taskId:    a.taskId    || null,
});

const _normNote = (n) => ({
  id:        n.id,
  type:      'note',
  who:       n.author || n.who || 'You',
  what:      'added a note',
  subject:   n.title || '',
  detail:    n.body || n.detail || '',
  createdAt: n.createdAt || null,
  personId:  n.personId  || null,
  companyId: n.companyId || null,
});

/* ── type → CSS tone class ── */
const TONE_MAP = {
  email: 'tone-blue', email_received: 'tone-blue', deal: 'tone-blue',
  deal_created: 'tone-blue', deal_updated: 'tone-blue', deal_stage_changed: 'tone-blue',
  deal_won: 'tone-blue', notification_sent: 'tone-blue',
  meeting_completed: 'tone-green', call: 'tone-green', whatsapp: 'tone-green',
  meeting: 'tone-purple', task: 'tone-purple', task_created: 'tone-purple',
  task_updated: 'tone-purple', task_completed: 'tone-purple', follower_added: 'tone-purple',
  task_started: 'tone-blue', task_archived: 'tone-gray',
  note: 'tone-amber', followup: 'tone-amber', reminder: 'tone-amber',
  workflow_event: 'tone-amber', comment: 'tone-amber',
  deal_lost: 'tone-red', task_cancelled: 'tone-red',
};
const _tone = (t) => (t && TONE_MAP[t]) || 'tone-gray';

/* ── type → icon name ── */
const _icon = (t) => {
  const m = {
    email: 'Mail', email_received: 'Inbox',
    meeting: 'Calendar', meeting_completed: 'CheckCircle',
    call: 'Phone', whatsapp: 'MessageCircle',
    note: 'StickyNote',
    deal: 'Target', deal_created: 'Target', deal_updated: 'Target',
    deal_stage_changed: 'Target', deal_won: 'Award', deal_lost: 'XCircle',
    task: 'CheckSquare', task_created: 'CheckSquare', task_updated: 'CheckSquare',
    task_completed: 'CheckCircle', task_cancelled: 'XCircle',
    task_started: 'Play', task_archived: 'Archive',
    followup: 'Clock', reminder: 'Bell', activity: 'Activity',
    comment: 'MessageCircle', document: 'File',
    follower_added: 'Eye', workflow_event: 'Zap', notification_sent: 'Bell',
  };
  return m[t] || 'Activity';
};

/* ── type → human label ── */
const _label = (t) => {
  const m = {
    email: 'Email Sent', email_received: 'Email Received',
    meeting: 'Meeting Scheduled', meeting_completed: 'Meeting Completed',
    call: 'Call', whatsapp: 'WhatsApp', note: 'Note Added',
    deal: 'Deal Update', deal_created: 'Deal Created', deal_updated: 'Deal Updated',
    deal_stage_changed: 'Stage Changed', deal_won: 'Deal Won', deal_lost: 'Deal Lost',
    task: 'Task', task_created: 'Task Created', task_updated: 'Task Updated',
    task_completed: 'Task Completed', task_cancelled: 'Task Cancelled',
    task_started: 'Task Started', task_archived: 'Task Archived',
    followup: 'Follow-up', reminder: 'Reminder', activity: 'Activity',
    comment: 'Comment', document: 'Document',
    follower_added: 'Follower Added', workflow_event: 'Workflow', notification_sent: 'Notification',
  };
  return m[t] || (t ? t.replaceAll('_', ' ') : 'Activity');
};

/* ── filter matching (Activities = anything not email/meeting/note) ── */
const EMAIL_TYPES   = new Set(['email','email_received']);
const MEETING_TYPES = new Set(['meeting','meeting_completed']);
const NON_ACT_TYPES = new Set(['email','email_received','meeting','meeting_completed','note']);

/* For deal Timeline: exclude types that have dedicated tabs.
   Keeps only deal/system property-change events (deal_created, deal_stage_changed, etc.) */
const DEAL_TL_EXCLUDE = new Set([
  'email','email_received','meeting','meeting_completed',
  'call','whatsapp','note',
  'task','task_created','task_updated','task_completed',
  'task_cancelled','task_started','task_archived',
]);

const _filterMatch = (ev, filter) => {
  if (filter === 'All')        return true;
  if (filter === 'Emails')     return EMAIL_TYPES.has(ev.type);
  if (filter === 'Meetings')   return MEETING_TYPES.has(ev.type);
  if (filter === 'Notes')      return ev.type === 'note';
  if (filter === 'Activities') return !NON_ACT_TYPES.has(ev.type);
  return true;
};

/* ── scope key by entity type ── */
const SCOPE_KEYS = { deal: 'dealId', company: 'companyId', person: 'personId', task: 'taskId' };

/* ── normalize a Promise.allSettled result to a plain array ── */
const _resolve = (r, altKey) => {
  if (r.status !== 'fulfilled') return null;
  const v = r.value;
  if (Array.isArray(v)) return v;
  return (altKey && v?.[altKey]) || v?.data || v?.items || [];
};

/* ── days-since → tone/color (no nested ternaries) ── */
const _dayTone = (days) => {
  if (days == null) return 'tone-gray';
  if (days > 30)    return 'tone-red';
  if (days > 14)    return 'tone-amber';
  return 'tone-green';
};

const _dayColor = (days) => {
  if (days == null) return 'var(--nx-text-faint)';
  if (days > 30)    return 'var(--nx-danger)';
  if (days > 14)    return 'var(--nx-warning)';
  return 'var(--nx-success)';
};

/* ── detail line for a timeline event ── */
const _eventDetail = (event, t) => {
  if (t === 'email')             return `To: ${event.to || '—'}`;
  if (t === 'email_received')    return `From: ${event.from || event.sender || '—'}`;
  if (t === 'comment')           return (event.body || event.text || '').slice(0, 140);
  if (t === 'follower_added')    return `${event.followerName || event.name || 'Someone'} is now following this record`;
  if (t === 'workflow_event')    return event.trigger ? `Trigger: ${event.trigger}` : '';
  if (t === 'notification_sent') return event.title || event.body || '';
  if (t === 'meeting' && event.scheduledAt)
    return new Date(event.scheduledAt).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
  return event.detail || '';
};

/* ── filter config (for chips) ── */
const COMM_FILTERS = [
  { id: 'All',        label: 'All' },
  { id: 'Emails',     label: 'Emails' },
  { id: 'Meetings',   label: 'Meetings' },
  { id: 'Activities', label: 'Activities' },
  { id: 'Notes',      label: 'Notes' },
];

/* ══════════════════════════════════════════════════════════════
   TIMELINE EVENT P4 — uses CSS system classes throughout
   ══════════════════════════════════════════════════════════════ */
const TimelineEventP4 = ({ event, onNavigate, canNav }) => {
  const t         = event.type || 'activity';
  const tone      = _tone(t);
  const iconName  = _icon(t);
  const typeLabel = _label(t);

  /* Primary display text */
  const primary = event.subject || event.title || event.workflowName || event.what || '';

  /* Detail line — logic lives in module-level _eventDetail to stay below complexity threshold */
  let detail = _eventDetail(event, t);
  if (detail.length > 140) detail = detail.slice(0, 140) + '…';

  const ts = _relTimeP4(_getDateStr(event));

  /* Use a real <button> when navigatable so assistive devices activate it correctly */
  const El        = canNav ? 'button' : 'div';
  const elProps   = canNav ? { type: 'button', onClick: () => onNavigate(event) } : {};

  return (
    <El
      className={'nx-timeline-event' + (canNav ? ' is-nav' : '')}
      {...elProps}
    >
      <div className={'nx-timeline-dot ' + tone}>
        <NexusIcon name={iconName} size={13} />
      </div>
      <div className="nx-timeline-body">
        <div className="nx-timeline-header">
          <span className="nx-tl-type">{typeLabel}</span>
          {event.who && event.who !== '—' && <span className="nx-tl-actor">{event.who}</span>}
          <span className="nx-tl-time" style={{ marginLeft: 'auto' }}>{ts}</span>
          {canNav && <NexusIcon name="ChevronRight" size={11} style={{ color: 'var(--nx-text-faint)', flexShrink: 0, marginLeft: 4 }} />}
        </div>
        {primary && <div className="nx-tl-primary">{primary}</div>}
        {detail  && <div className="nx-timeline-target">{detail}</div>}
      </div>
    </El>
  );
};

/* ══════════════════════════════════════════════════════════════
   CUSTOMER TIMELINE VIEW — owns all state; full Enterprise UI
   type: 'person' | 'company' | 'deal'  (singular, from call site)
   ══════════════════════════════════════════════════════════════ */
const CustomerTimelineView = ({ record, type }) => {
  const wsId = window.__nx_auth?.user?.activeWorkspaceId;

  const [activities,  setActivities]  = useState([]);
  const [emails,      setEmails]      = useState([]);
  const [meetings,    setMeetings]    = useState([]);
  const [notes,       setNotes]       = useState([]);
  const [loading,     setLoading]     = useState(false);
  const [error,       setError]       = useState(null);
  const [filter,      setFilter]      = useState('All');
  const [refreshKey,  setRefreshKey]  = useState(0);
  const [,            setTick]        = useState(0);

  const scopeKey = SCOPE_KEYS[type] || 'personId';

  /* Live timestamp refresh — re-renders every 60 s so relative times stay accurate (R-03) */
  useEffect(() => {
    const timer = setInterval(() => setTick(t => t + 1), 60_000);
    return () => clearInterval(timer);
  }, []);

  /* Navigate to a related record from a timeline event (R-01).
     Priority: deal → person → company. Skips the current record. */
  const handleEventNav = useCallback((event) => {
    const store = window.__nx_store;
    const api   = window.__nx_api;
    if (!store?.openDrawer || !api || !wsId) return;
    if (event.dealId && event.dealId !== record?.id) {
      api.deal?.getOne?.(wsId, event.dealId)
        ?.then(d => d && store.openDrawer(d))
        ?.catch(() => {});
    } else if (event.personId && type !== 'person' && event.personId !== record?.id) {
      api.people?.getOne?.(wsId, event.personId)
        ?.then(p => p && store.openDrawer(p))
        ?.catch(() => {});
    } else if (event.companyId && type !== 'company' && event.companyId !== record?.id) {
      api.company?.getOne?.(wsId, event.companyId)
        ?.then(c => c && store.openDrawer(c))
        ?.catch(() => {});
    }
  }, [record?.id, type, wsId]);

  /* Fetch all timeline data. Uses a `cancelled` flag so that if record/workspace
     changes before the async operations complete, stale state updates are dropped. */
  useEffect(() => {
    if (!record?.id || !wsId) return;
    let cancelled = false;

    setLoading(true);
    setError(null);

    const api    = window.__nx_api;
    const params = { [scopeKey]: record.id };

    /* Tasks + deals skip email/meeting fetches — no FK linkage (task) or covered by dedicated tabs (deal) */
    const emailFn = (type !== 'task' && type !== 'deal') ? api?.email?.listEmails : null;
    const meetFn  = (type !== 'task' && type !== 'deal') ? api?.meetings?.list    : null;
    const actFn   = api?.activity?.list;
    const noteFn  = (type !== 'deal' && type !== 'task') ? api?.note?.list : null;

    (async () => {
      const [emailsR, meetingsR, actsR, notesR] = await Promise.allSettled([
        emailFn ? emailFn(wsId, params)                                        : Promise.resolve([]),
        meetFn  ? meetFn(wsId, params)                                         : Promise.resolve([]),
        actFn   ? actFn(wsId, { ...params, pageSize: 100, sortOrder: 'desc' }) : Promise.resolve({ data: [] }),
        noteFn  ? noteFn(wsId, params)                                         : Promise.resolve([]),
      ]);

      if (cancelled) return;

      const emailArr = _resolve(emailsR);
      const meetArr  = _resolve(meetingsR);
      const actArr   = _resolve(actsR);
      const noteArr  = _resolve(notesR, 'notes');

      if (![emailArr, meetArr, actArr, noteArr].some(Boolean)) {
        setError('Unable to load timeline');
        setLoading(false);
        return;
      }

      if (emailArr) setEmails(emailArr);
      if (meetArr)  setMeetings(meetArr);
      if (actArr)   setActivities(actArr.map(_normAct));
      if (noteArr)  setNotes(noteArr.map(_normNote));
      setLoading(false);
    })();

    return () => { cancelled = true; };
  }, [wsId, record?.id, type, scopeKey, refreshKey]);

  /* ── All events merged, deduplicated by id, sorted newest-first ── */
  const allEvents = useMemo(() => {
    /* Email direction: raw API uses `type` field ('INCOMING'/'OUTGOING'), some
       normalizers map it to 'received'/'sent'. Check both to detect inbound. */
    const emailEvs = emails.map(e => {
      const isIncoming = e.direction === 'INCOMING'
        || e.type === 'INCOMING' || e.type === 'received' || e.type === 'email_received';
      return { ...e, type: isIncoming ? 'email_received' : 'email' };
    });
    const meetingEvs = meetings.map(m => ({ ...m, type: m.completedAt ? 'meeting_completed' : 'meeting' }));

    /* Exclude activity-log entries for types the dedicated APIs already cover (R-02).
       Prevents double-entries when the backend logs both sides of the same event. */
    const hasEmailApi   = emails.length > 0;
    const hasMeetingApi = meetings.length > 0;
    const filteredActs  = activities.filter(a => {
      if (hasEmailApi   && EMAIL_TYPES.has(a.type))   return false;
      if (hasMeetingApi && MEETING_TYPES.has(a.type)) return false;
      return true;
    });

    /* Deduplicate by id — guards against any remaining same-ID overlap */
    const seen = new Set();
    const merged = [...filteredActs, ...emailEvs, ...meetingEvs, ...notes]
      .filter(e => {
        if (!e.id) return true;
        if (seen.has(e.id)) return false;
        seen.add(e.id);
        return true;
      });

    /* Deal Timeline: keep only deal/system property-change events; comms belong to dedicated tabs */
    const scoped = type === 'deal' ? merged.filter(e => !DEAL_TL_EXCLUDE.has(e.type)) : merged;

    return scoped.sort((a, b) => {
      const da = new Date(_getDateStr(a) || 0);
      const db = new Date(_getDateStr(b) || 0);
      return db - da;
    });
  }, [activities, emails, meetings, notes]);

  /* ── Stats ── */
  const stats = useMemo(() => {
    const emailCount   = allEvents.filter(e => ['email','email_received'].includes(e.type)).length;
    const meetingCount = allEvents.filter(e => ['meeting','meeting_completed'].includes(e.type)).length;
    const actCount     = allEvents.filter(e => !['email','email_received','meeting','meeting_completed','note'].includes(e.type)).length;
    const allDates     = allEvents.map(e => _getDateStr(e)).filter(Boolean).map(d => new Date(d).getTime());
    const lastMs       = allDates.length ? Math.max(...allDates) : 0;
    const daysSince    = lastMs ? Math.floor((Date.now() - lastMs) / 86400000) : null;
    return { emails: emailCount, meetings: meetingCount, activities: actCount, daysSince };
  }, [allEvents]);

  /* ── Filtered + grouped events ── */
  const grouped = useMemo(() => {
    const filtered = allEvents.filter(e => _filterMatch(e, filter));
    const map      = new Map();
    filtered.forEach(e => {
      const k    = _dayKey(_getDateStr(e));
      const list = map.get(k) || [];
      list.push(e);
      map.set(k, list);
    });
    return [...map.entries()];
  }, [allEvents, filter]);

  /* ── Per-filter counts (for chip labels) ── */
  const counts = useMemo(() => {
    const c = { All: allEvents.length };
    COMM_FILTERS.slice(1).forEach(f => { c[f.id] = allEvents.filter(e => _filterMatch(e, f.id)).length; });
    return c;
  }, [allEvents]);

  /* ── Stat card definitions ── */
  const dayTone  = _dayTone(stats.daysSince);
  const dayColor = _dayColor(stats.daysSince);
  const dayValue = stats.daysSince ?? '—';

  const STAT_CARDS = [
    { id: 'Emails',    label: 'Emails',             value: stats.emails,    icon: 'Mail',     tone: 'tone-blue',   valueColor: 'var(--nx-text)' },
    { id: 'Meetings',  label: 'Meetings',           value: stats.meetings,  icon: 'Calendar', tone: 'tone-purple', valueColor: 'var(--nx-text)' },
    { id: 'Activities',label: 'Activities',         value: stats.activities,icon: 'Activity', tone: 'tone-green',  valueColor: 'var(--nx-text)' },
    { id: null,        label: 'Days Since Contact', value: dayValue,         icon: 'Clock',    tone: dayTone,       valueColor: dayColor },
  ];

  const handleStatClick = (id) => {
    if (!id || loading) return;
    setFilter(prev => prev === id ? 'All' : id);
  };

  const currentFilterLabel = COMM_FILTERS.find(f => f.id === filter)?.label.toLowerCase() || '';
  const isTask    = type === 'task';
  const skelItems = isTask ? [0] : [0,1,2,3];
  let emptyDesc = 'No activity, notes, emails, or meetings recorded yet.';
  if (isTask)        emptyDesc = 'No activity recorded for this task yet.';
  else if (type === 'deal') emptyDesc = 'No deal history events recorded yet.';
  const statCards = isTask
    ? (
        <div className="nx-tl-stat">
          <div className="nx-timeline-dot tone-green">
            <NexusIcon name="Activity" size={13} />
          </div>
          <div className="nx-tl-stat-body">
            <div className="nx-tl-stat-value">{stats.activities}</div>
            <div className="nx-tl-stat-label">Activities</div>
          </div>
        </div>
      )
    : STAT_CARDS.map(s => {
        const isActive = filter === s.id;
        const El       = s.id ? 'button' : 'div';
        return (
          <El
            key={s.label}
            type={s.id ? 'button' : undefined}
            className={'nx-tl-stat' + (isActive ? ' is-active' : '')}
            onClick={s.id ? () => handleStatClick(s.id) : undefined}
            aria-pressed={s.id ? isActive : undefined}
          >
            <div className={'nx-timeline-dot ' + s.tone}>
              <NexusIcon name={s.icon} size={13} />
            </div>
            <div className="nx-tl-stat-body">
              <div className="nx-tl-stat-value" style={{ color: s.valueColor }}>{s.value}</div>
              <div className="nx-tl-stat-label">{s.label}</div>
            </div>
          </El>
        );
      });

  return (
    <div className="nx-drawer-page nx-tl-root">

      {/* ── Stat cards — hidden for deal type (comms live in dedicated tabs) ── */}
      {type !== 'deal' && (
        <div className="nx-tl-stats">
          {loading ? skelItems.map(i => <div key={i} className="nx-tl-skel-stat" />) : statCards}
        </div>
      )}

      {/* ── Filter chips + refresh ── */}
      <div className="nx-tl-filter-row">
        {type !== 'task' && type !== 'deal' && (
        <div className="nx-tl-chips">
          {COMM_FILTERS.map(f => (
            <button
              key={f.id}
              type="button"
              className={'nx-chip' + (filter === f.id ? ' is-active' : '')}
              disabled={loading}
              onClick={() => !loading && setFilter(f.id)}
              aria-pressed={filter === f.id}
            >
              {f.label}{!loading && counts[f.id] > 0 && filter !== f.id ? ` · ${counts[f.id]}` : ''}
            </button>
          ))}
        </div>
        )}
        <button
          type="button"
          className="nx-iconbtn"
          onClick={() => setRefreshKey(k => k + 1)}
          disabled={loading}
          title="Refresh timeline"
          aria-label="Refresh timeline"
        >
          <NexusIcon
            name="RefreshCw"
            size={13}
            style={loading ? { animation: 'nx-spin 0.8s linear infinite' } : {}}
          />
        </button>
      </div>

      {/* ── Feed ── */}
      {error && !loading ? (
        <div className="nx-empty-state is-compact">
          <div className="nx-empty-state-icon">
            <NexusIcon name="AlertCircle" size={20} />
          </div>
          <div className="nx-empty-state-title">Failed to load timeline</div>
          <div className="nx-empty-state-desc">Check your connection and try again.</div>
          <div className="nx-empty-state-action">
            <button type="button" className="nx-btn nx-btn-ghost nx-btn-sm" onClick={() => setRefreshKey(k => k + 1)}>
              <NexusIcon name="RefreshCw" size={12} />Retry
            </button>
          </div>
        </div>
      ) : (
        <div className="nx-tl-feed">

          {/* Loading skeleton */}
          {loading && [0,1,2,3,4].map(i => (
            <div key={i} className="nx-tl-skel-row">
              <div className="nx-tl-skel-dot" />
              <div className="nx-tl-skel-body">
                <div className="nx-skeleton-line" style={{ width: '55%' }} />
                <div className="nx-skeleton-line" style={{ width: '38%' }} />
              </div>
            </div>
          ))}

          {/* Empty state */}
          {!loading && grouped.length === 0 && (
            <div className="nx-empty-state is-compact">
              <div className="nx-empty-state-icon">
                <NexusIcon name="Activity" size={20} />
              </div>
              <div className="nx-empty-state-title">No events yet</div>
              <div className="nx-empty-state-desc">
                {filter !== 'All' ? `No ${currentFilterLabel} recorded for this record.` : emptyDesc}
              </div>
            </div>
          )}

          {/* Grouped events */}
          {!loading && grouped.map(([day, evs]) => (
            <div key={day} className="nx-timeline-group">
              <div className="nx-timeline-date-label">{day}</div>
              <div className="nx-timeline-events">
                {evs.map((e, i) => {
                  const canNav = Boolean(
                    (e.dealId    && e.dealId    !== record?.id)                       ||
                    (e.personId  && type !== 'person'  && e.personId  !== record?.id) ||
                    (e.companyId && type !== 'company' && e.companyId !== record?.id)
                  );
                  return (
                    <TimelineEventP4
                      key={e.id || i}
                      event={e}
                      onNavigate={handleEventNav}
                      canNav={canNav}
                    />
                  );
                })}
              </div>
            </div>
          ))}

        </div>
      )}

    </div>
  );
};

/* CommunicationTimeline kept as alias for backward compat */
const CommunicationTimeline = CustomerTimelineView;

/* ── registrations ── */
window.TimelineEventP4      = TimelineEventP4;
window.CommunicationTimeline = CommunicationTimeline;
window.CustomerTimelineView  = CustomerTimelineView;
